import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Divider,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ALERT_SEVERITY } from '../../utils/trlConstants';
import { useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserOrganizations } from '../../hooks/useOrganization';
import OrgSwitcher from '../../components/OrgSwitcher';

/**
 * Health Status Messages — Mindmap branch 3, "Outputs → Health Status Messages"
 *   - Alerts: flagged on dashboards, emails, SMS messaging
 *   - Recommendations
 * LIVE: shows real Hugo_HealthMessages (written by Workflow 6 — Divergence
 * Detection) for the active org. Empty state until the first live message arrives.
 */
function HealthMessages() {
  const [filter, setFilter] = useState('all');
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const orgId = organizations[0]?.id || null;
  const [liveMessages, setLiveMessages] = useState([]);

  useEffect(() => {
    if (!orgId) {
      setLiveMessages([]);
      return undefined;
    }
    const q = query(collection(db, 'Hugo_HealthMessages'), where('organizationId', '==', orgId));
    return onSnapshot(
      q,
      (snap) => {
        const msgs = snap.docs.map((d) => {
          const m = { id: d.id, ...d.data() };
          const t = m.triggeredAt;
          return { ...m, triggeredAt: t?.toDate ? t.toDate() : t, live: true };
        });
        msgs.sort((a, b) => new Date(b.triggeredAt) - new Date(a.triggeredAt));
        setLiveMessages(msgs);
      },
      () => setLiveMessages([])
    );
  }, [orgId]);

  const isLive = liveMessages.length > 0;
  const messages = liveMessages;

  const acknowledge = async (msg) => {
    if (!msg.live) return;
    try {
      await updateDoc(doc(db, 'Hugo_HealthMessages', msg.id), { acknowledged: true });
    } catch (e) {
      console.error('Could not acknowledge health message:', e);
    }
  };

  const filtered = filter === 'all'
    ? messages
    : messages.filter((m) => m.severity === filter);

  const channelIcon = (ch) => {
    if (ch === 'dashboard') return <DashboardIcon fontSize="small" />;
    if (ch === 'email') return <EmailIcon fontSize="small" />;
    return <SmsIcon fontSize="small" />;
  };

  const sevColor = (sev) => ALERT_SEVERITY.find((s) => s.value === sev)?.color || '#666';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <HealthAndSafetyIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4">OE Health Alerts</Typography>
        {isLive && (
          <Chip label="LIVE" size="small" sx={{ bgcolor: '#2e7d32', color: '#fff', fontWeight: 700 }} />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <OrgSwitcher />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Real-time health alerts and recommendations from DREW. Warning and critical alerts also reach your inbox automatically — manage that in Settings → Notification Preferences.
      </Typography>

      {liveMessages.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No health alerts yet. DREW writes an alert here when survey scores (perception) and confirmed OE Conditions (reality) diverge.
        </Alert>
      )}

      <ToggleButtonGroup
        size="small"
        exclusive
        value={filter}
        onChange={(_, v) => v && setFilter(v)}
        sx={{ mb: 3 }}
      >
        <ToggleButton value="all">All ({messages.length})</ToggleButton>
        {ALERT_SEVERITY.map((s) => (
          <ToggleButton key={s.value} value={s.value}>
            {s.label} ({messages.filter((m) => m.severity === s.value).length})
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Stack spacing={2}>
        {filtered.map((msg) => (
          <Card key={msg.id} sx={{ borderLeft: 6, borderLeftColor: sevColor(msg.severity), opacity: msg.acknowledged ? 0.6 : 1 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip
                      label={msg.severity.toUpperCase()}
                      size="small"
                      sx={{ bgcolor: sevColor(msg.severity), color: '#fff', fontWeight: 700 }}
                    />
                    {msg.quadrant && <Chip label={msg.quadrant} size="small" variant="outlined" />}
                    {msg.acknowledged && <Chip icon={<CheckCircleIcon />} label="Acknowledged" size="small" color="success" variant="outlined" />}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {new Date(msg.triggeredAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700}>{msg.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {msg.summary}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      <LightbulbIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Recommendations
                    </Typography>
                    <List dense disablePadding>
                      {(msg.recommendations || []).map((r, i) => (
                        <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sevColor(msg.severity) }} />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">{r}</Typography>} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    Sources: {(msg.sources || []).join(' • ')}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="overline" color="text.secondary">Routed to</Typography>
                  <Stack spacing={0.75} sx={{ mt: 0.5, mb: 2 }}>
                    {(msg.channels || []).map((c) => (
                      <Box key={c} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                        {channelIcon(c)}
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{c}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  {!msg.acknowledged && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined">Snooze</Button>
                      <Button size="small" variant="contained" onClick={() => acknowledge(msg)}>Acknowledge</Button>
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

export default HealthMessages;
